import type { CvrBaseline, ResponseBreakdown } from "../api";

type Props = {
  responses: ResponseBreakdown[];
  optionLabelMap: Record<string, string>;
  denominator: "answered" | "started";
  isMulti: boolean;
  kind: "options" | "free_text";
  // The quiz's own CVR over the same window/cohort/persona, computed in the
  // same request. The per-row delta is read against it; an absolute rate is
  // not decidable without it.
  baseline: CvrBaseline;
  // The server's suppression floor, echoed so the label states the real
  // number. The UI does NOT apply it -- `cvr === null` is the signal, and it
  // stays the signal when this is `null`: with no number to state, the label
  // says "too few" and no threshold, rather than inventing a plausible one.
  cvrMinOrders: number | null;
};

function fmtPct(p: number): string {
  return `${p.toFixed(2)}%`;
}

function fmtRank(idx: number): string {
  return String(idx + 1).padStart(2, "0");
}

// Single source of truth for the delta's sign: the formatted string and the
// "is this an improvement" flag used for the class name must never be able to
// drift, so both are derived from the same computation here.
function computeDelta(rowCvr: number, baselineCvr: number): { text: string; isPositive: boolean } {
  const pp = rowCvr - baselineCvr;
  const isPositive = pp >= 0;
  const sign = isPositive ? "+" : "-";
  return { text: `${sign}${Math.abs(pp).toFixed(2)}pp`, isPositive };
}

export function ResponseBreakdownRow({
  responses,
  optionLabelMap,
  denominator,
  isMulti,
  kind,
  baseline,
  cvrMinOrders,
}: Props) {
  if (kind === "free_text") {
    return (
      <div className="px-6 py-5 text-sm text-mars-white/60">
        Free-text response — see Submissions tab.
      </div>
    );
  }

  // Hide responses tied to option ids that no longer exist in the current
  // quiz definition. Stale ids would otherwise add noise (e.g. <1% rows from
  // an option that was removed mid-experiment).
  const visible = responses.filter((r) => optionLabelMap[r.answer_id] !== undefined);

  if (visible.length === 0) {
    return (
      <div className="px-6 py-5 text-sm text-mars-white/60">
        No answers in range.
      </div>
    );
  }

  return (
    <div className="space-y-4 px-6 py-5">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <div className="eyebrow text-mars-white/55">
          Response breakdown
        </div>
        {isMulti && (
          <div className="mono text-[0.6rem] uppercase tracking-[0.18em] text-mars-orange/80">
            Multi-select · totals may exceed 100%
          </div>
        )}
      </div>
      <div className="mono text-[0.6rem] uppercase tracking-[0.18em] text-mars-white/40">
        Orders · CVR vs whole quiz; later questions read high
      </div>
      <div className="mono text-[0.6rem] normal-case tracking-normal text-mars-white/40">
        Quiz-attributed orders within 24h, first-party. Will not match Triple
        Whale, which attributes differently -- a gap is expected, not an outage.
      </div>
      <div className="space-y-2">
        {visible.map((r, i) => {
          const label = optionLabelMap[r.answer_id] ?? r.answer_id;
          const pct =
            denominator === "answered" ? r.pct_of_answered : r.pct_of_started;
          const isLeader = i === 0;
          return (
            <div
              key={r.answer_id}
              className="grid grid-cols-[2.25rem_minmax(8rem,16rem)_1fr_7.5rem_4.5rem_7rem] items-center gap-3 text-sm"
            >
              <div
                className={
                  isLeader
                    ? "mono text-[0.7rem] font-bold tabular-nums text-mars-orange"
                    : "mono text-[0.7rem] tabular-nums text-mars-white/35"
                }
              >
                {fmtRank(i)}
              </div>
              <div
                className={
                  isLeader
                    ? "truncate font-medium text-mars-white"
                    : "truncate text-mars-white/80"
                }
                title={label}
              >
                {label}
              </div>
              <div className="relative h-2.5 overflow-hidden rounded-full bg-mars-line/40">
                <div
                  className={
                    isLeader
                      ? "absolute inset-y-0 left-0 rounded-full bg-mars-orange"
                      : "absolute inset-y-0 left-0 rounded-full bg-mars-orange/50"
                  }
                  style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                  data-testid={`bar-${r.answer_id}`}
                />
              </div>
              <div className="mono flex items-baseline justify-end gap-2 tabular-nums">
                <span
                  className={
                    isLeader
                      ? "text-base font-bold text-mars-white"
                      : "text-sm text-mars-white/85"
                  }
                >
                  {fmtPct(pct)}
                </span>
                <span className="text-[0.65rem] uppercase tracking-[0.12em] text-mars-white/40">
                  {r.count.toLocaleString("en-US")}
                </span>
              </div>
              <div
                className="mono justify-self-end text-[0.7rem] tabular-nums text-mars-white/55"
                data-testid={`orders-${r.answer_id}`}
                title="Quiz-attributed orders within 24h of the last quiz event"
              >
                {/* deploy-admin.yml and deploy-aws.yml are separate workflows
                    with separate concurrency groups; the admin (S3 sync) can
                    land before the API (Docker build + CloudFormation +
                    alembic) finishes. In that window the API still returns
                    responses with no `orders`/`cvr` keys, so `r.orders` can be
                    `undefined` -- fall back to 0 rather than crash the tree
                    (there is no ErrorBoundary in admin/src/). */}
                {(r.orders ?? 0).toLocaleString("en-US")}
              </div>
              <div
                className="mono flex flex-col items-end leading-tight tabular-nums"
                data-testid={`cvr-${r.answer_id}`}
              >
                {/* Loose check deliberately: an older API in the deploy-skew
                    window above omits `cvr` entirely (`undefined`), not just
                    `null`. Both must render as "too few / suppressed", never
                    reach `.toFixed` on a missing value. */}
                {r.cvr == null ? (
                  <span
                    className="text-[0.7rem] uppercase tracking-[0.1em] text-mars-white/35"
                    title={
                      cvrMinOrders === null
                        ? "Too few orders to state a rate"
                        : `Fewer than ${cvrMinOrders} orders -- too few to state a rate`
                    }
                  >
                    {cvrMinOrders === null ? "too few" : <>n&lt;{cvrMinOrders}</>}
                  </span>
                ) : (
                  <>
                    <span className="text-sm text-mars-white/85">
                      {r.cvr.toFixed(2)}%
                    </span>
                    {baseline.cvr !== null && (() => {
                      const delta = computeDelta(r.cvr, baseline.cvr);
                      return (
                        <span
                          className={
                            delta.isPositive
                              ? "text-[0.65rem] text-mars-orange"
                              : "text-[0.65rem] text-mars-white/45"
                          }
                          data-testid={`cvr-delta-${r.answer_id}`}
                        >
                          {delta.text}
                        </span>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
